import { useEffect, useMemo, useRef, useState } from "react";
import { cycleSummaryForBadge, fetchCycleOperatorSummary, fetchEntryObservability } from "./api";
import { summarizeTradeCycleBadge } from "./tradeCycle";
import type { CycleOperatorSummary, EntryObservabilityResponse } from "./types";

/** Faster than full dashboard refresh — trade count is operator-critical. */
export const TRADE_CYCLE_REFRESH_MS = 4_000;
/** Entry observability is heavy (~20s cold); poll slower than cycle summary. */
export const ENTRY_OBSERVABILITY_REFRESH_MS = 12_000;
const STALE_AFTER_MS = 18_000;

export type TradeCycleProgressState = {
  cycle?: CycleOperatorSummary;
  entry?: EntryObservabilityResponse;
  loading: boolean;
  refreshedAt?: string;
  fetchError?: string;
  stale: boolean;
  badge: ReturnType<typeof summarizeTradeCycleBadge>;
};

/** Cohort identity for entry observability requests — must match across parallel refreshes. */
export function cycleCohortFingerprint(cycle: CycleOperatorSummary | undefined | null): string {
  if (!cycle) return "";
  return `${cycle.protocolVersion ?? ""}|${cycle.createdAfter ?? ""}|${cycle.nextReviewAtFills ?? ""}`;
}

export function cycleCohortsDiffer(
  a: CycleOperatorSummary | undefined,
  b: CycleOperatorSummary | undefined,
): boolean {
  return cycleCohortFingerprint(a) !== cycleCohortFingerprint(b);
}

/** Guards against out-of-order entry observability responses after cohort or refresh changes. */
export function shouldApplyEntryFetchResult(params: {
  mounted: boolean;
  aborted: boolean;
  fetchGeneration: number;
  currentRefreshGeneration: number;
  expectedCohortFingerprint: string;
  currentCohortFingerprint: string;
}): boolean {
  if (!params.mounted) return false;
  if (params.aborted) return false;
  if (params.fetchGeneration !== params.currentRefreshGeneration) return false;
  if (params.currentCohortFingerprint !== params.expectedCohortFingerprint) return false;
  return true;
}

export function shouldRefreshEntryObservability(params: {
  nowMs: number;
  lastEntryRefreshAtMs: number;
  hasEntry: boolean;
  cohortChanged: boolean;
}): boolean {
  if (!params.hasEntry || params.cohortChanged) return true;
  return params.nowMs - params.lastEntryRefreshAtMs >= ENTRY_OBSERVABILITY_REFRESH_MS;
}

export function useTradeCycleProgress(): TradeCycleProgressState {
  const [cycle, setCycle] = useState<CycleOperatorSummary | undefined>();
  const [entry, setEntry] = useState<EntryObservabilityResponse | undefined>();
  const [cycleLive, setCycleLive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<string | undefined>();
  const [fetchError, setFetchError] = useState<string | undefined>();
  const [tickNow, setTickNow] = useState(() => Date.now());
  const lastGoodCycle = useRef<CycleOperatorSummary | undefined>(undefined);
  const lastGoodEntry = useRef<EntryObservabilityResponse | undefined>(undefined);
  const lastEntryRefreshAt = useRef(0);
  const lastEntryCohortFingerprint = useRef("");
  const refreshGeneration = useRef(0);
  const entryAbortController = useRef<AbortController | undefined>(undefined);

  useEffect(() => {
    let mounted = true;
    let timer: number | undefined;
    let pending = false;

    function scheduleRefresh() {
      window.clearTimeout(timer);
      if (mounted) timer = window.setTimeout(refresh, TRADE_CYCLE_REFRESH_MS);
    }

    function cycleFillsKnown(cycleData?: CycleOperatorSummary) {
      const target = cycleData ?? lastGoodCycle.current;
      if (target?.protocolVersion === "profit-v1") {
        if (
          target.improvementPhase === "awaiting-improvement-ship" ||
          (target.improvementPhase === "post-review" && target.nextReviewAtFills == null)
        ) {
          return target.fills != null || target.fillsFromHandoff != null;
        }
        return (
          target.fillsFromLedger != null ||
          target.fillsSource === "ledger_live" ||
          target.fillsSource === "authoritative_guard"
        );
      }
      return (
        target?.fills != null || target?.fillsFromHandoff != null || target?.fillsFromLedger != null
      );
    }

    function applyCycleSuccess(cycleData: CycleOperatorSummary) {
      lastGoodCycle.current = cycleData;
      setCycle(cycleData);
      setCycleLive(true);
      if (cycleFillsKnown(cycleData)) {
        setLoading(false);
        setRefreshedAt(new Date().toISOString());
        setFetchError(undefined);
      }
    }

    async function refreshEntry(
      cycleData: CycleOperatorSummary | undefined,
      signal: AbortSignal,
      expectedCohortFingerprint: string,
      fetchGeneration: number,
    ) {
      try {
        const entryData = await fetchEntryObservability(cycleData, signal);
        if (
          !shouldApplyEntryFetchResult({
            mounted,
            aborted: signal.aborted,
            fetchGeneration,
            currentRefreshGeneration: refreshGeneration.current,
            expectedCohortFingerprint,
            currentCohortFingerprint: cycleCohortFingerprint(lastGoodCycle.current),
          })
        ) {
          return;
        }
        lastGoodEntry.current = entryData;
        lastEntryRefreshAt.current = Date.now();
        lastEntryCohortFingerprint.current = expectedCohortFingerprint;
        setEntry(entryData);
        setFetchError(undefined);
        setRefreshedAt(new Date().toISOString());
      } catch (entryError) {
        if (signal.aborted || !mounted) return;
        if (fetchGeneration !== refreshGeneration.current) return;
        if (lastGoodEntry.current) setEntry(lastGoodEntry.current);
        if (!cycleFillsKnown()) {
          setFetchError(entryError instanceof Error ? entryError.message : "연결 확인 중");
        }
      }
    }

    function queueEntryRefresh(
      cycleData: CycleOperatorSummary | undefined,
      fetchGeneration: number,
      cohortFingerprint: string,
      force = false,
    ) {
      const cohortChanged = cohortFingerprint !== lastEntryCohortFingerprint.current;
      if (
        !force &&
        !shouldRefreshEntryObservability({
          nowMs: Date.now(),
          lastEntryRefreshAtMs: lastEntryRefreshAt.current,
          hasEntry: Boolean(lastGoodEntry.current),
          cohortChanged,
        })
      ) {
        return;
      }
      entryAbortController.current?.abort();
      const entryAbort = new AbortController();
      entryAbortController.current = entryAbort;
      void refreshEntry(cycleData, entryAbort.signal, cohortFingerprint, fetchGeneration);
    }

    async function refresh() {
      if (document.hidden) {
        scheduleRefresh();
        return;
      }
      if (pending) {
        scheduleRefresh();
        return;
      }
      pending = true;
      const fetchGeneration = ++refreshGeneration.current;

      try {
        const cycleData = await fetchCycleOperatorSummary();
        if (!mounted || fetchGeneration !== refreshGeneration.current) return;

        applyCycleSuccess(cycleData);

        const cohortFingerprint = cycleCohortFingerprint(cycleData);
        queueEntryRefresh(cycleData, fetchGeneration, cohortFingerprint);
      } catch (error) {
        if (!mounted || fetchGeneration !== refreshGeneration.current) return;
        if (lastGoodCycle.current) setCycle(lastGoodCycle.current);
        if (lastGoodEntry.current) setEntry(lastGoodEntry.current);
        setFetchError(error instanceof Error ? error.message : "연결 확인 중");
        setLoading(!lastGoodCycle.current && !cycleFillsKnown());
      } finally {
        pending = false;
        scheduleRefresh();
      }
    }

    void refresh();
    queueEntryRefresh(
      undefined,
      refreshGeneration.current,
      cycleCohortFingerprint(undefined),
      true,
    );

    function onVisibilityChange() {
      if (!document.hidden && mounted) {
        scheduleRefresh();
        void refresh();
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      mounted = false;
      entryAbortController.current?.abort();
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const clock = window.setInterval(() => setTickNow(Date.now()), 1000);
    return () => window.clearInterval(clock);
  }, []);

  const stale = useMemo(() => {
    if (!refreshedAt) return false;
    const age = tickNow - Date.parse(refreshedAt);
    return !Number.isFinite(age) || age > STALE_AFTER_MS;
  }, [refreshedAt, tickNow]);

  const badge = useMemo(
    () =>
      summarizeTradeCycleBadge(entry, {
        loading: loading || !cycleLive,
        refreshedAt,
        stale,
        fetchError,
        cycle: cycleLive ? cycleSummaryForBadge(cycle) : undefined,
      }),
    [entry, loading, cycleLive, refreshedAt, stale, fetchError, cycle],
  );

  return { cycle, entry, loading, refreshedAt, fetchError, stale, badge };
}
